import React, { useMemo } from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import config from 'constants/config'
import { useIntl } from 'react-intl'

interface Props {
  totalSeatsRemaining: number
  totalUsedSeatsOnCart: number
  purchaseLimit: App.ExperiencePurchaseLimit
  className?: string
}

function ExperienceTicketCustomerLimit(props: Props) {
  const { totalSeatsRemaining, totalUsedSeatsOnCart, purchaseLimit, className } = props
  const intl = useIntl()

  const { message, kind } = useMemo(() => {
    let message = ''
    let kind: 'warning' | 'critical' = 'warning'
    if (totalSeatsRemaining <= 0) {
      kind = 'critical'
      if (purchaseLimit.maxReason === 'AVAILABILITY') {
        message = intl.formatMessage({
          id: 'vbj/ZPDHUG',
          defaultMessage: 'You have reached the available slots for this session.',
          description: 'Critical message when all available slots in an experience session are taken',
        })
      } else if (purchaseLimit.maxReason === 'LIMIT-PER-CUSTOMER') {
        message = intl.formatMessage(
          {
            id: 'Vg930kO7WM',
            defaultMessage: 'You have reached the maximum purchase limit of {count, plural, one {# participant} other {# participants}} per account for this experience.',
            description: 'Critical message when user reaches per-account participant purchase limit',
          },
          { count: purchaseLimit.max },
        )
      } else if (purchaseLimit.maxReason === 'LIMIT-PER-ORDER') {
        message = intl.formatMessage(
          {
            id: 'XHjYuRSJkS',
            defaultMessage: 'You have reached the maximum purchase limit of {count, plural, one {# participant} other {# participants}} per order for this experience.',
            description: 'Critical message when user reaches per-order participant purchase limit',
          },
          { count: purchaseLimit.max },
        )
      }
    } else {
      const MIN_SLOTS_REMAINING_TO_SHOW_MESSAGE = 3
      if (purchaseLimit.min > 1 && totalUsedSeatsOnCart < purchaseLimit.min) {
        message = intl.formatMessage(
          {
            id: 'dQ04aGlYLd',
            defaultMessage: 'Minimum {count, plural, one {# participant} other {# participants}} for this experience.',
            description: 'Warning message showing minimum number of participants required for an experience',
          },
          { count: purchaseLimit.min },
        )
      } else if (totalSeatsRemaining < MIN_SLOTS_REMAINING_TO_SHOW_MESSAGE) {
        if (purchaseLimit.maxReason === 'AVAILABILITY') {
          message = intl.formatMessage(
            {
              id: 'eW4GZU559F',
              defaultMessage: 'Only {count, plural, one {# slot} other {# slots}} left in this session.',
              description: 'Warning message showing few remaining slots in an experience session',
            },
            { count: purchaseLimit.max },
          )
        } else if (purchaseLimit.maxReason === 'LIMIT-PER-CUSTOMER') {
          message = intl.formatMessage(
            {
              id: 'NR7SdFZO7Z',
              defaultMessage: 'Maximum purchase limit of {count, plural, one {# participant} other {# participants}} per account for this experience.',
              description: 'Warning message showing per-account participant purchase limit when few slots remain',
            },
            { count: purchaseLimit.max },
          )
        } else if (purchaseLimit.maxReason === 'LIMIT-PER-ORDER') {
          message = intl.formatMessage(
            {
              id: 'Sj521xY2Q8',
              defaultMessage: 'Maximum purchase limit of {count, plural, one {# participant} other {# participants}} per order for this experience.',
              description: 'Warning message showing per-order participant purchase limit when few slots remain',
            },
            { count: purchaseLimit.max },
          )
        }
      }
    }

    return {
      message, kind,
    }
  }, [intl, totalSeatsRemaining, purchaseLimit, totalUsedSeatsOnCart])

  if (!message || !config.EXPERIENCES_PURCHASE_CUSTOMER_LIMIT) return null

  return (
    <MessageBanner className={className} kind={kind} icon={<LineInfoCircleIcon />}>
      {message}
    </MessageBanner>
  )
}

export default ExperienceTicketCustomerLimit
